import { Button, Grid, Typography } from '@material-ui/core/';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Link from 'next/link';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDeliveryTime } from '~/store/legacy/actions/shoppingCartActions';
import AppToolBar from '~/components/AppToolBar';
import AddressSection from '~/components/checkout/AddressSection';
import MealsSection from '~/components/checkout/MealsSection';
import PaymentSection from '~/components/checkout/PaymentSection';
import PlaceOrderButton from '~/components/checkout/PlaceOrderButton';
import ShoppingCart from '~/components/checkout/ShoppingCart.jsx';
import DeliveryTimePicker from '~/components/DeliveryTimePicker.jsx';
import DinerNotice from '~/components/DinerNotice.jsx';
import RequireLogin from '~/components/RequireLogin.jsx';
import Subtitle from '~/components/ui-kit/Subtitle/Subtitle';
import { SERVICE_TYPE } from '~/constant';
import {
  selectGeneralCartErrors,
  selectMeals,
  selectShoppingCartData,
} from '~/store/legacy/selector/shoppingCartSelectors';
import { selectUser, selectUserServiceRegionId } from '~/store/legacy/selector/userSelectors';
import { googleTagManager } from '~/third_party/GoogleTagManager';
import Page from '../components/Page';
import { fbEvents } from '~/third_party/Facebook';
import { AppsflyerPBA } from '~/third_party/Appsflyer';

const useStyles = makeStyles((theme) => ({
  checkoutPage: {
    marginBottom: theme.spacing(10),
  },
  field: {
    marginBottom: 30,
  },
  membership: {
    [theme.breakpoints.down('sm')]: {
      height: 540,
    },
    height: 450,
    marginLeft: 1,
    marginTop: 20,
  },
  infoContainer: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 40,
    },
  },
  infoField: {
    marginBottom: 40,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 20,
    },
  },
  shoppingCartField: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: -theme.spacing(2),
    },
  },
  placeOrderButtonContainer: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'inherit',
      position: 'sticky',
      bottom: 0,
      marginBottom: theme.spacing(2),
    },
  },
  placeOrderButton: {
    width: '100%',
  },
  errorHeader: {
    color: '#C60000',
    backgroundColor: '#F3F3F3',
    padding: 20,
    textAlign: 'center',
    fontWeight: 700,
  },
}));

//Delivery times
function DeliveryTimes({ error }) {
  const [dispatch, classes] = [useDispatch(), useStyles()];
  const { serviceType, times } = useSelector(selectShoppingCartData);
  const selectDeliveryTime = (time) => {
    dispatch(setDeliveryTime(time));
  };
  return (
    <Grid container className={classes.field}>
      <Grid item xs={12}>
        <Subtitle hLevel={6}>
          {serviceType === SERVICE_TYPE.PICKUP ? 'Pickup Window' : 'Delivery Window'}
        </Subtitle>
      </Grid>
      <Grid item xs={12}>
        {(serviceType === SERVICE_TYPE.DELIVERY || serviceType === SERVICE_TYPE.PICKUP) && (
          <DeliveryTimePicker
            deliveryTimes={times}
            onSelect={selectDeliveryTime}
            error={error}
            isPickup={serviceType === SERVICE_TYPE.PICKUP}
          />
        )}
      </Grid>
    </Grid>
  );
}

//ShoppingCart
function ShoppingCartDetail() {
  const classes = useStyles();
  return (
    <Grid
      container
      className={classNames(classes.field, classes.shoppingCartField)}
      justify='center'>
      <ShoppingCart outline fullWidth variant='place_order' />
    </Grid>
  );
}

function ErrorHeader() {
  const classes = useStyles();
  return (
    <div className={classes.errorHeader}>
      <Typography variant='h6'>
        We’re sorry! There seems to be an issue on this page. Please correct all errors and try
        again.
      </Typography>
    </div>
  );
}

function CheckoutPage() {
  const classes = useStyles();
  const { addressId, serviceType, meals } = useSelector(selectShoppingCartData);
  const serviceRegionId = useSelector(selectUserServiceRegionId);

  const cartErrors = useSelector(selectGeneralCartErrors);
  const errorDisplay =
    cartErrors &&
      Object.keys(cartErrors).length === 0 &&
      cartErrors.constructor === Object ? null : (
      <Grid item xs={12}>
        <ErrorHeader />
      </Grid>
    );
  meals
  useEffect(() => {
    // do this only on initial render
    googleTagManager.trackCheckoutPageView();
    fbEvents.trackCheckoutPageView();
    AppsflyerPBA.trackWebInitiatedCheckout({
      meals: (meals || []).map(AppsflyerPBA.getMeal),
      address_id: addressId,
      service_type: serviceType,
      service_region: serviceRegionId,
    });
  }, []);

  return (
    <Grid container className={classes.checkoutPage}>
      <Grid container spacing={4}>
        {errorDisplay}
        <DinerNotice addressId={addressId} />
        <Grid item md={8} xs={12}>
          <AddressSection isDelivery={serviceType === SERVICE_TYPE.DELIVERY} />
          <DeliveryTimes error={cartErrors['deliveryWindow']} />
          <PaymentSection />
          <MealsSection />
        </Grid>
        <Grid item md={4} xs={12}>
          <Subtitle hLevel={6}>Checkout</Subtitle>
          <ShoppingCartDetail />
        </Grid>
        {/* Sticky button that takes over in mobile size */}
        <Grid item xs={12} className={classes.placeOrderButtonContainer}>
          <PlaceOrderButton className={classes.placeOrderButton} />
        </Grid>
      </Grid>
    </Grid>
  );
}

function EmptyCart() {
  const classes = useStyles();
  return (
    <Grid container direction='column' justify='center' className={classes.shoppingCart}>
      <Typography align='center' variant='body1'>
        No item found in your shopping cart.
      </Typography>
      <Link href='/'>
        <Button variant='text'>
          <Typography color='primary' align='center' variant='body1'>
            Go to find some food you love!
          </Typography>
        </Button>
      </Link>
    </Grid>
  );
}

function Checkout() {
  const user = useSelector(selectUser);
  const { meals } = useSelector(selectShoppingCartData);
  if (!user) {
    return null;
  }
  return (
    <Page name='checkout'>
      <AppToolBar />
      {meals.length === 0 ? (
        <Grid container justify='center' alignItems='center' style={{ height: '60vh' }}>
          <EmptyCart />
        </Grid>
      ) : (
        <CheckoutPage />
      )}
    </Page>
  );
}

export default RequireLogin(Checkout);
